import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Users, Award, Calendar, Trophy, User as UserIcon, Mail, LogOut, TrendingUp, ClipboardList, Video, UserCheck, Film, Dumbbell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { logout } = useAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { title: 'Manage Players', icon: Users, path: '/admin/players', description: 'Unified players view: roster cards, accounts, premium, and team assignments' },
    { title: 'Team Media', icon: Film, path: '/admin/team-media', description: 'Team videos and tournament standouts' },
    { title: 'Manage Commits', icon: Award, path: '/admin/commits', description: 'Update college commitments' },
    { title: 'Manage Showcases', icon: Calendar, path: '/admin/showcases', description: 'Add upcoming or past showcases' },
    { title: 'Showcase Registrations', icon: ClipboardList, path: '/admin/showcase-registrations', description: 'View player registrations per showcase' },
    { title: 'Manage Standouts', icon: Trophy, path: '/admin/standouts', description: 'Highlight top performers' },
    { title: 'Manage Profiles', icon: UserIcon, path: '/admin/profiles', description: 'Update founders and advisors' },
    { title: 'Featured Videos', icon: Video, path: '/admin/videos', description: 'Upload and manage homepage videos' },
    { title: 'Training & Nutrition', icon: Dumbbell, path: '/admin/programs', description: 'Upload monthly training & nutrition PDFs for premium members' },
    { title: 'Contact Submissions', icon: Mail, path: '/admin/contacts', description: 'View registration forms' }
  ];

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 className="display-large">Admin Dashboard</h1>
              <p className="body-large">Manage Pacific Ghost Baseball Content</p>
            </div>
            <button onClick={handleLogout} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LogOut size={20} /> Logout
            </button>
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {/* Quick Stats Widget */}
          <div data-testid="quick-stats-widget" style={{ 
            marginBottom: '32px', 
            padding: '24px', 
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', 
            borderRadius: '16px',
            border: '1px solid var(--border-medium)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <TrendingUp size={24} style={{ color: 'var(--brand-primary)' }} />
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'white' }}>Quick Stats</h2>
            </div>
            {loadingStats ? (
              <p style={{ color: 'var(--text-light)' }}>Loading stats...</p>
            ) : stats ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', 
                gap: '16px' 
              }}>
                <Link to="/admin/players" data-testid="stat-youth-players" style={{ 
                  padding: '16px', 
                  background: 'rgba(212, 175, 55, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  textAlign: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Users size={28} style={{ color: 'var(--brand-primary)', marginBottom: '8px' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{stats.youth_players}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Youth Players</div>
                </Link>
                <Link to="/admin/players" data-testid="stat-hs-players" style={{ 
                  padding: '16px', 
                  background: 'rgba(212, 175, 55, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  textAlign: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Users size={28} style={{ color: 'var(--brand-primary)', marginBottom: '8px' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{stats.hs_players}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>HS Players</div>
                </Link>
                <Link to="/admin/commits" data-testid="stat-commits" style={{ 
                  padding: '16px', 
                  background: 'rgba(212, 175, 55, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  textAlign: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Award size={28} style={{ color: 'var(--brand-primary)', marginBottom: '8px' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{stats.commits}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Commits</div>
                </Link>
                <Link to="/admin/showcases" data-testid="stat-showcases" style={{ 
                  padding: '16px', 
                  background: 'rgba(212, 175, 55, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  textAlign: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Calendar size={28} style={{ color: 'var(--brand-primary)', marginBottom: '8px' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{stats.showcases}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Showcases</div>
                </Link>
                <Link to="/admin/standouts" data-testid="stat-standouts" style={{ 
                  padding: '16px', 
                  background: 'rgba(212, 175, 55, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  textAlign: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Trophy size={28} style={{ color: 'var(--brand-primary)', marginBottom: '8px' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{stats.standouts}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Standouts</div>
                </Link>
                <Link to="/admin/profiles" data-testid="stat-profiles" style={{ 
                  padding: '16px', 
                  background: 'rgba(212, 175, 55, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  textAlign: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <UserIcon size={28} style={{ color: 'var(--brand-primary)', marginBottom: '8px' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{stats.profiles}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Profiles</div>
                </Link>
                <Link to="/admin/contacts" data-testid="stat-contacts" style={{ 
                  padding: '16px', 
                  background: 'rgba(212, 175, 55, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  textAlign: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Mail size={28} style={{ color: 'var(--brand-primary)', marginBottom: '8px' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{stats.contacts}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Contacts</div>
                </Link>
              </div>
            ) : (
              <p style={{ color: 'var(--text-light)' }}>Unable to load stats</p>
            )}
          </div>

          <div className="network-grid">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.path} to={item.path} className="network-card" style={{ textDecoration: 'none' }}>
                  <Icon size={48} style={{ color: 'var(--brand-primary)', marginBottom: '16px' }} />
                  <h3 className="network-card-title">{item.title}</h3>
                  <p className="network-card-content">{item.description}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;